import { corrigeDuplicacaoNome } from '@modulos/home/submodulos/dados-meteorologicos/utils';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { Estacao } from '@home/submodulos/dados-meteorologicos/interfaces/filtros-opcoes';
import { PostosRelatorios } from '@home/submodulos/dados-meteorologicos/interfaces/tabela-relatorio';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS_QUANTIS,
	InstanciaRelatorio,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import { handleGenerateQuantisDataHighchart } from '@home/submodulos/dados-meteorologicos/utils/graficoUtils';
import { Select } from '@layout/interfaces/select';
import {
	formatarDataPadraoMesAno,
	criarImagemBase64FromChart,
	baixarEAbrirPDF,
	verificaSePDFEAdicionaNaTabela,
	isNuloOuUndefined,
} from '@utils';
import * as pdfseira from '@utils/pdf-seira';
import { Chart } from 'highcharts';
import * as Highcharts from 'highcharts';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { Meses, PeriodoChuvosoMap } from '../../../enum';
import {
	DadoGraficoQuantis,
	DadosGraficoQuantisMensal,
	QuantisPrecipitacao,
	QuantisResponse,
} from '../../../interfaces';
import {
	QuantisMicrorregiaoService,
	RelatoriosService,
} from '../../../services';
import { gerarFilenameGrafico } from '../../../utils';
import { DateTimeUtils } from '@utils/datetime-util';
import { DocumentExporter } from '@utils/document-exporter';
import * as ExcelTable from 'mr-excel';
import { numberToBrNumberTables } from '../../../../../../../shared/utils/index';

@Component({
	selector: 'seira-pluviometro-grafico-quantis',
	templateUrl: './pluviometro-grafico-quantis.component.html',
	styleUrls: ['./pluviometro-grafico-quantis.component.scss'],
})
export class PluviometroGraficoQuantisComponent
	implements OnInit, OnDestroy, InstanciaRelatorio
{
	inputs = inject(INPUTS_RELATORIOS_QUANTIS);
	microrregioes!: Select<string>[];
	postos: PostosRelatorios[] = [];
	estacoes: Estacao[] = [];
	chart!: Chart;
	botoesDeExportacao: GroupButton[] = [
		{
			label: '.png',
			size: 'small',
			icon: 'ph-image',
			onClick: () => this.exportarPNG(),
		},
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () => {
				return this.exportarPDF();
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];

	loading = true;
	gerou = false;
	useMaximumLimit = 500;
	resultado?: DadosGraficoQuantisMensal;
	private subscription = new Subscription();
	private _destroyed = new Subject();

	descricaoRelatorio = '';
	descricaoRelatorioMensal =
		'Define-se como quantis mensais, os valores que dividem uma amostra de dados mensais em percentis, permitindo a análise de diferentes categorias pluviométricas em determinado local ou região.';
	descricaoRelatorioAnual =
		'Define-se como quantis anuais, os valores que dividem uma amostra de dados anuais em percentis, permitindo a análise de diferentes categorias pluviométricas em determinado local ou região.';
	descricaoRelatorioPeriodoChuvoso =
		'Define-se como quantis por período chuvoso os valores que segmentam a precipitação acumulada ao longo de diferentes períodos, sejam eles anuais, quadrimestrais, quinquemestrais ou semestrais, dividindo-a em percentis para analisar a distribuição da chuva em uma microrregião.';

	periodoTitulo = '';
	quantisTitulo = '';
	tituloXAxis = '';

	dadosMensal: QuantisResponse<QuantisPrecipitacao[]>;
	dadosAnual: QuantisResponse<QuantisPrecipitacao[]>;
	dadosPeriodoChuvoso: QuantisResponse<any[]>;
	ano: number;

	constructor(
		private toastr: ToastrService,
		private readonly quantisService: QuantisMicrorregiaoService,
		private relatorioService: RelatoriosService
	) {}

	ngOnInit(): void {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValue(this.inputs.agrupamentos[0].value, { emitEvent: false });
	}

	ngOnDestroy() {
		this.clearValidators();
		this.subscription.unsubscribe();
		this._destroyed.next(undefined);
	}

	gerarRelatorio() {
		this.ano = this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.value.getFullYear();
		if (this.inputs.form.invalid) {
			return;
		}
		this.loading = true;
		this.inputs.setLoading(true);
		const periodoBusca = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
			?.value;

		if (periodoBusca === 'mensal') {
			this.tituloXAxis = 'Meses';
			this.quantisTitulo = 'mensal';
			this.descricaoRelatorio = this.descricaoRelatorioMensal;
		} else if (periodoBusca === 'anual') {
			this.tituloXAxis = 'Anos';
			this.quantisTitulo = 'anual';
			this.descricaoRelatorio = this.descricaoRelatorioAnual;
		} else {
			this.tituloXAxis = 'Anos';
			this.quantisTitulo = 'por região pluviométrica';
			this.descricaoRelatorio = this.descricaoRelatorioPeriodoChuvoso;
		}

		this.setQuantisByPeriodoBusca(periodoBusca);
	}

	setQuantisByPeriodoBusca(periodoBusca: string) {
		this.clearValidators();
		const agrupamento = this.inputs.form.get('agrupamento')?.value;

		if (agrupamento === 'REGIAO_PLUVIOMETRICA') {
			if (periodoBusca === 'periodo_chuvoso') {
				this.getQuantisByRegiaoAndPeriodoChuvoso();
				this.setValidatorsQuantisPluviometricoPeriodoChuvoso();
			} else if (periodoBusca === 'mensal') {
				this.getQuantisMensalByRegiao();
				this.setValidatorsQuantisPluviometricoMensalOrAnual();
			} else if (periodoBusca === 'anual') {
				this.getQuantisAnualByRegiao();
				this.setValidatorsQuantisPluviometricoMensalOrAnual();
			}
		} else {
			if (periodoBusca === 'mensal') {
				this.getQuantisMensal();
				this.setValidatorsQuantisMensalOrAnual();
			} else if (periodoBusca === 'anual') {
				this.getQuantisAnual();
				this.setValidatorsQuantisMensalOrAnual();
			}
		}
	}

	getQuantisByRegiaoAndPeriodoChuvoso() {
		const periodoChuvoso = this.inputs.form.get('periodoChuvoso')
			?.value as string;
		const regiao = this.inputs.form.get('regiao')?.value;
		const mapeamentoQtd: Record<string, number> = {
			QUADRA_CHUVOSA: 10,
			QUINTETO_CHUVOSO: 10,
			SEMESTRE_CHUVOSO: 10,
		};
		const hoje = new Date();
		this.quantisService
			.getUltimosQuantisByRegiaoAndPeriodoChuvoso(
				regiao,
				periodoChuvoso,
				mapeamentoQtd[periodoChuvoso]
			)
			.subscribe({
				next: resp => {
					this.relatorioService.atualizaVisibilidadeBotoesExportacao(resp);
					this.dadosPeriodoChuvoso = resp;
					const anos = resp.quantis
						.map(q => q.ano)
						.filter((ano): ano is number => typeof ano === 'number');
					const dadosGraficosQuantis: DadoGraficoQuantis[] =
						resp.quantis.map<DadoGraficoQuantis>(q => ({
							titulo: q.ano?.toString(),
							categorias: handleGenerateQuantisDataHighchart(q),
						}));
					this.periodoTitulo = anos.length
						? `${Math.min(...anos)} a ${Math.max(...anos)}`
						: `${
								hoje.getFullYear() - mapeamentoQtd[periodoChuvoso] + 1
						  } a ${hoje.getFullYear()}`;
					const periodo = PeriodoChuvosoMap.get(
						this.inputs.form.get(FormularioRelatorio.PERIODO_CHUVOSO)?.value
					);
					const tituloGeral = `Região: ${resp.regiao} - Período: ${periodo} (${resp?.periodoChuvoso}) - ${this.periodoTitulo}`;
					const fileName = gerarFilenameGrafico(
						`quantis_regiao_periodo_chuvoso`
					);
					this.resultado = {
						tituloGeral,
						fileName,
						legenda:
							'Os limiares utilizados para classificar a precipitação acumulada no período chuvoso, na geração dos gráficos e mapas de quantis, têm como referência o estudo intitulado: Determinação De Limiares para A Precipitação Mensal Das Regiões Homogêneas Da Paraíba Usando Quantis dos autores: Silva, E. A. da, Brito, J. I. B. de, Becker, C. T., Mandú, T. B., & Lima, I. P. C., publicado em 2022, pela Revista Brasileira de Climatologia ISSN - 2237-8642.',
						dadosGraficosQuantis,
					};
					this.setMaximumLimit(resp);
				},
				error: () => {
					this.resultado = undefined;
					this.gerou = false;
					this.loading = false;
					this.inputs.setLoading(false);
					this.toastr.error('Erro ao obter informações do posto selecionado');
				},
				complete: () => {
					this.gerou = true;
					this.loading = false;
					this.inputs.setLoading(false);
				},
			});
	}

	getQuantisMensalByRegiao() {
		const mesInicio = this.inputs.form.get(FormularioRelatorio.DATA_INICIO)
			?.value;
		const mesFim = this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.value;
		const regiao = this.inputs.form.get('regiao')?.value;

		this.quantisService
			.getQuantisMensalGraficoByRegiaoId(regiao, mesInicio, mesFim)
			.subscribe({
				next: resp => {
					this.dadosMensal = resp;
					const dadosGraficosQuantis: DadoGraficoQuantis[] = resp.quantis.map(
						q => {
							const newCategorias = handleGenerateQuantisDataHighchart(
								q.valores
							);

							return {
								titulo: `${Meses[q.data! - 1]}`,
								categorias: newCategorias,
							};
						}
					);
					this.periodoTitulo = DateTimeUtils.formatarDataPeriodo(
						mesInicio,
						mesFim,
						'mensal',
						false
					);
					const tituloGeral = `Região pluviométrica: ${resp.regiao}  - ${this.periodoTitulo}`;
					const legenda =
						'Os limiares utilizados para definir a precipitação mensal, na geração dos gráficos e mapas de quantis originaram-se do estudo intitulado: Determinação De Limiares para A Precipitação Mensal Das Regiões Homogêneas Da Paraíba Usando Quantis dos autores: Silva, E. A. da, Brito, J. I. B. de, Becker, C. T., Mandú, T. B., & Lima, I. P. C., publicado em 2022, pela Revista Brasileira de Climatologia ISSN - 2237-8642.';
					const fileName = gerarFilenameGrafico(`quantis_regiao_anual`);
					this.resultado = {
						tituloGeral,
						legenda,
						dadosGraficosQuantis,
						fileName,
					};
					this.setMaximumLimit(resp);
				},
				error: () => {
					this.resultado = undefined;
					this.gerou = false;
					this.loading = false;
					this.inputs.setLoading(false);
					this.toastr.error('Erro ao obter informações do posto selecionado');
				},
				complete: () => {
					this.gerou = true;
					this.loading = false;
					this.inputs.setLoading(false);
				},
			});
	}

	getQuantisAnualByRegiao() {
		const anoInicio = this.inputs.form.get(FormularioRelatorio.DATA_INICIO)
			?.value;
		const anoFim = this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.value;
		const regiao = this.inputs.form.get('regiao')?.value;

		this.quantisService
			.getQuantisAnualGraficoByRegiaoId(regiao, anoInicio, anoFim)
			.subscribe({
				next: resp => {
					this.dadosAnual = resp;
					const dadosGraficosQuantis: DadoGraficoQuantis[] = resp.quantis.map(
						q => {
							const newCategorias = handleGenerateQuantisDataHighchart(
								q.valores
							);

							return {
								titulo: q.data!.toString(),
								categorias: newCategorias,
							};
						}
					);
					this.periodoTitulo = DateTimeUtils.formatarDataPeriodo(
						anoInicio,
						anoFim,
						'anual',
						false
					);
					const tituloGeral = `Região pluviométrica: ${resp.regiao}  - ${this.periodoTitulo}`;
					const legenda =
						'Os limiares utilizados para definir a precipitação mensal, na geração dos gráficos e mapas de quantis originaram-se do estudo intitulado: Determinação De Limiares para A Precipitação Mensal Das Regiões Homogêneas Da Paraíba Usando Quantis dos autores: Silva, E. A. da, Brito, J. I. B. de, Becker, C. T., Mandú, T. B., & Lima, I. P. C., publicado em 2022, pela Revista Brasileira de Climatologia ISSN - 2237-8642.';
					const fileName = gerarFilenameGrafico(`quantis_regiao_anual`);
					this.resultado = {
						tituloGeral,
						legenda,
						dadosGraficosQuantis,
						fileName,
					};
					this.setMaximumLimit(resp);
				},
				error: () => {
					this.resultado = undefined;
					this.gerou = false;
					this.loading = false;
					this.inputs.setLoading(false);
					this.toastr.error('Erro ao obter informações do posto selecionado');
				},
				complete: () => {
					this.gerou = true;
					this.loading = false;
					this.inputs.setLoading(false);
				},
			});
	}

	getQuantisMensal() {
		const mesInicio = this.inputs.form.get(FormularioRelatorio.DATA_INICIO)
			?.value;
		const mesFim = this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.value;
		const estacao = Number.parseInt(this.inputs.form.get('estacao')?.value);

		this.periodoTitulo = DateTimeUtils.formatarDataPeriodo(
			mesInicio,
			mesFim,
			'mensal',
			false
		);
		this.quantisService
			.getQuantisMensalGraficoByEstacaoId(estacao, mesInicio, mesFim)
			.subscribe({
				next: resp => {
					this.relatorioService.atualizaVisibilidadeBotoesExportacao(resp);
					this.dadosMensal = resp;
					const dadosGraficosQuantis: DadoGraficoQuantis[] = resp.quantis.map(
						q => {
							const newCategorias = handleGenerateQuantisDataHighchart(
								q.valores
							);

							return {
								titulo: `${Meses[q.data! - 1]}`,
								categorias: newCategorias,
							};
						}
					);

					this.setMaximumLimit(resp);

					const tituloGeral =
						resp.posto === resp.municipio
							? `Posto: ${resp.posto} - ${this.periodoTitulo}`
							: `Posto: ${resp.posto} (${resp.municipio}) - ${this.periodoTitulo}`;
					const fileName = gerarFilenameGrafico(
						`quantis_mensal_${resp.posto?.toLocaleLowerCase()}`
					);
					const legenda =
						'Os limiares utilizados para definir a precipitação mensal, na geração dos gráficos e mapas de quantis originaram-se do estudo intitulado: Determinação De Limiares para A Precipitação Mensal Das Regiões Homogêneas Da Paraíba Usando Quantis dos autores: Silva, E. A. da, Brito, J. I. B. de, Becker, C. T., Mandú, T. B., & Lima, I. P. C., publicado em 2022, pela Revista Brasileira de Climatologia ISSN - 2237-8642.';

					this.resultado = {
						tituloGeral,
						fileName,
						legenda,
						dadosGraficosQuantis,
					};
				},
				error: () => {
					this.resultado = undefined;
					this.gerou = false;
					this.loading = false;
					this.inputs.setLoading(false);
					this.toastr.error('Erro ao obter informações do posto selecionado');
				},
				complete: () => {
					this.gerou = true;
					this.loading = false;
					this.inputs.setLoading(false);
				},
			});
	}

	getQuantisAnual() {
		const anoInicio = this.inputs.form.get(FormularioRelatorio.DATA_INICIO)
			?.value;
		const anoFim = this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.value;
		const estacao = Number.parseInt(this.inputs.form.get('estacao')?.value);

		this.periodoTitulo = DateTimeUtils.formatarDataPeriodo(
			anoInicio,
			anoFim,
			'anual',
			false
		);
		this.quantisService
			.getQuantisAnualGraficoByEstacaoId(estacao, anoInicio, anoFim)
			.subscribe({
				next: resp => {
					this.relatorioService.atualizaVisibilidadeBotoesExportacao(resp);
					this.dadosAnual = resp;
					const dadosGraficosQuantis: DadoGraficoQuantis[] = resp.quantis.map(
						q => {
							const newCategorias = handleGenerateQuantisDataHighchart(
								q.valores
							);

							return {
								titulo: q.data!.toString(),
								categorias: newCategorias,
							};
						}
					);

					const tituloGeral =
						resp.posto === resp.municipio
							? `Posto: ${resp.posto} - ${this.periodoTitulo}`
							: `Posto: ${resp.posto} (${resp.municipio}) - ${this.periodoTitulo}`;
					const fileName = gerarFilenameGrafico(
						`quantis_anual_${resp.posto?.toLocaleLowerCase()}`
					);
					const legenda =
						'Os limiares utilizados para definir a precipitação mensal, na geração dos gráficos e mapas de quantis originaram-se do estudo intitulado: Determinação De Limiares para A Precipitação Mensal Das Regiões Homogêneas Da Paraíba Usando Quantis dos autores: Silva, E. A. da, Brito, J. I. B. de, Becker, C. T., Mandú, T. B., & Lima, I. P. C., publicado em 2022, pela Revista Brasileira de Climatologia ISSN - 2237-8642.';

					this.resultado = {
						tituloGeral,
						fileName,
						legenda,
						dadosGraficosQuantis,
					};
					this.setMaximumLimit(resp);
				},
				error: () => {
					this.resultado = undefined;
					this.gerou = false;
					this.loading = false;
					this.inputs.setLoading(false);
					this.toastr.error('Erro ao obter informações do posto selecionado');
				},
				complete: () => {
					this.gerou = true;
					this.loading = false;
					this.inputs.setLoading(false);
				},
			});
	}

	onChartCreated(chart: Chart) {
		this.chart = chart;
	}

	getTipo() {
		const periodoBusca = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
			?.value;

		return periodoBusca === 'mensal'
			? 'QUANTIS_MENSAL'
			: periodoBusca === 'anual'
			? 'QUANTIS_ANUAL'
			: 'QUANTIS_PERIODO_CHUVOSO';
	}

	setMaximumLimit(resp: any) {
		let todosValores;
		const periodoBusca = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
			?.value;

		if (periodoBusca === 'periodo_chuvoso') {
			todosValores = resp.quantis.flatMap(
				({
					muitoSeco,
					seco,
					normal,
					chuvoso,
					muitoChuvoso,
					observado,
				}: any) => [muitoSeco, seco, normal, chuvoso, muitoChuvoso, observado]
			);
		} else {
			todosValores = resp.quantis.flatMap(
				({ valores }: any) => Object.values(valores) as number[]
			);
		}

		const valorMaximo = Math.max(...todosValores);
		this.useMaximumLimit = valorMaximo > 10 ? valorMaximo + 10 : 10;
	}

	getDadosTabelaParaExportacao(isPdf: boolean) {
		const tableData: any[][] = [];
		const colunas: any[] = [];
		const agrupamento = this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)
			?.value;

		if (
			this.getTipo() === 'QUANTIS_MENSAL' ||
			this.getTipo() === 'QUANTIS_ANUAL'
		) {
			colunas.push(
				{
					text:
						agrupamento === 'MUNICIPIO_POSTO'
							? 'Município/Posto'
							: 'Região Pluviométrica',
					fillColor: '#DCDCDC',
				},
				{
					text: 'Data',
					fillColor: '#DCDCDC',
					alignment: 'center',
				},
				{ text: 'Muito seco (mm)', fillColor: '#DCDCDC', alignment: 'center' },
				{ text: 'Seco (mm)', fillColor: '#DCDCDC', alignment: 'center' },
				{ text: 'Normal (mm)', fillColor: '#DCDCDC', alignment: 'center' },
				{ text: 'Chuvoso (mm)', fillColor: '#DCDCDC', alignment: 'center' },
				{
					text: 'Muito chuvoso (mm)',
					fillColor: '#DCDCDC',
					alignment: 'center',
				},
				{ text: 'Observado (mm)', fillColor: '#DCDCDC', alignment: 'center' }
			);

			verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);

			if (this.getTipo() === 'QUANTIS_MENSAL') {
				this.dadosMensal.quantis.forEach((quantis: QuantisPrecipitacao) => {
					const rowData: any[] = [];

					rowData.push(
						agrupamento === 'MUNICIPIO_POSTO'
							? corrigeDuplicacaoNome(
									`${this.dadosMensal.municipio}/${this.dadosMensal.posto}`
							  )
							: this.dadosMensal.regiao,
						this.alinharDadoPdf(
							formatarDataPadraoMesAno(quantis.data!, this.ano),
							isPdf
						),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.muitoSeco, 1),
							isPdf
						),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.seco, 1),
							isPdf
						),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.normal, 1),
							isPdf
						),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.chuvoso, 1),
							isPdf
						),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.muitoChuvoso, 1),
							isPdf
						),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.observado, 1),
							isPdf
						)
					);

					tableData.push(rowData);
				});
			} else {
				this.dadosAnual.quantis.forEach((quantis: QuantisPrecipitacao) => {
					const rowData: any[] = [];

					rowData.push(
						agrupamento === 'MUNICIPIO_POSTO'
							? corrigeDuplicacaoNome(
									`${this.dadosAnual.municipio}/${this.dadosAnual.posto}`
							  )
							: this.dadosAnual.regiao,
						this.alinharDadoPdf(quantis.data, isPdf),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.muitoSeco, 1),
							isPdf
						),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.seco, 1),
							isPdf
						),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.normal, 1),
							isPdf
						),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.chuvoso, 1),
							isPdf
						),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.muitoChuvoso, 1),
							isPdf
						),
						this.alinharDadoPdf(
							numberToBrNumberTables(quantis.valores.observado, 1),
							isPdf
						)
					);

					tableData.push(rowData);
				});
			}
		} else {
			colunas.push(
				{ text: 'Região pluviométrica', fillColor: '#DCDCDC' },
				{ text: 'Data', fillColor: '#DCDCDC', alignment: 'center' },
				{ text: 'Muito seco (mm)', fillColor: '#DCDCDC', alignment: 'center' },
				{ text: 'Seco (mm)', fillColor: '#DCDCDC', alignment: 'center' },
				{ text: 'Normal (mm)', fillColor: '#DCDCDC', alignment: 'center' },
				{ text: 'Chuvoso (mm)', fillColor: '#DCDCDC', alignment: 'center' },
				{
					text: 'Muito chuvoso (mm)',
					fillColor: '#DCDCDC',
					alignment: 'center',
				},
				{ text: 'Observado (mm)', fillColor: '#DCDCDC', alignment: 'center' }
			);

			verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);

			this.dadosPeriodoChuvoso.quantis.forEach(quantis => {
				const rowData: any[] = [];

				rowData.push(
					this.dadosPeriodoChuvoso.regiao,
					this.alinharDadoPdf(quantis.ano, isPdf),
					this.alinharDadoPdf(
						numberToBrNumberTables(quantis.muitoSeco, 1),
						isPdf
					),
					this.alinharDadoPdf(numberToBrNumberTables(quantis.seco, 1), isPdf),
					this.alinharDadoPdf(numberToBrNumberTables(quantis.normal, 1), isPdf),
					this.alinharDadoPdf(
						numberToBrNumberTables(quantis.chuvoso, 1),
						isPdf
					),
					this.alinharDadoPdf(
						numberToBrNumberTables(quantis.muitoChuvoso, 1),
						isPdf
					),
					this.alinharDadoPdf(
						numberToBrNumberTables(quantis.observado, 1),
						isPdf
					)
				);

				tableData.push(rowData);
			});
		}
		return tableData;
	}

	getDadosTabelaParaExportacaoXLSX() {
		const tableData: any[] = [];
		const agrupamento = this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)
			?.value;

		if (this.getTipo() === 'QUANTIS_MENSAL') {
			if (agrupamento === 'MUNICIPIO_POSTO') {
				this.dadosMensal.quantis.map(quantisMensal => {
					tableData.push({
						municipio: this.dadosMensal.municipio,
						data: formatarDataPadraoMesAno(quantisMensal.data!, this.ano),
						muitoSeco: isNuloOuUndefined(quantisMensal.valores.muitoSeco)
							? '-'
							: quantisMensal.valores.muitoSeco,
						seco: isNuloOuUndefined(quantisMensal.valores.seco)
							? '-'
							: quantisMensal.valores.seco,
						normal: isNuloOuUndefined(quantisMensal.valores.normal)
							? '-'
							: quantisMensal.valores.normal,
						chuvoso: isNuloOuUndefined(quantisMensal.valores.chuvoso)
							? '-'
							: quantisMensal.valores.chuvoso,
						muitoChuvoso: isNuloOuUndefined(quantisMensal.valores.muitoChuvoso)
							? '-'
							: quantisMensal.valores.muitoChuvoso,
						observado: isNuloOuUndefined(quantisMensal.valores.observado)
							? '-'
							: quantisMensal.valores.observado,
					});
				});
			} else {
				this.dadosMensal.quantis.map(quantisMensal => {
					tableData.push({
						regiao: this.dadosMensal.regiao,
						data: formatarDataPadraoMesAno(quantisMensal.data!, this.ano),
						muitoSeco: isNuloOuUndefined(quantisMensal.valores.muitoSeco)
							? '-'
							: quantisMensal.valores.muitoSeco,
						seco: isNuloOuUndefined(quantisMensal.valores.seco)
							? '-'
							: quantisMensal.valores.seco,
						normal: isNuloOuUndefined(quantisMensal.valores.normal)
							? '-'
							: quantisMensal.valores.normal,
						chuvoso: isNuloOuUndefined(quantisMensal.valores.chuvoso)
							? '-'
							: quantisMensal.valores.chuvoso,
						muitoChuvoso: isNuloOuUndefined(quantisMensal.valores.muitoChuvoso)
							? '-'
							: quantisMensal.valores.muitoChuvoso,
						observado: isNuloOuUndefined(quantisMensal.valores.observado)
							? '-'
							: quantisMensal.valores.observado,
					});
				});
			}
		} else if (this.getTipo() === 'QUANTIS_ANUAL') {
			if (agrupamento === 'MUNICIPIO_POSTO') {
				this.dadosAnual.quantis.map(quantisAnual => {
					tableData.push({
						municipio: this.dadosAnual.municipio,
						data: isNuloOuUndefined(quantisAnual.data)
							? '-'
							: quantisAnual.data,
						muitoSeco: isNuloOuUndefined(quantisAnual.valores.muitoSeco)
							? '-'
							: quantisAnual.valores.muitoSeco,
						seco: isNuloOuUndefined(quantisAnual.valores.seco)
							? '-'
							: quantisAnual.valores.seco,
						normal: isNuloOuUndefined(quantisAnual.valores.normal)
							? '-'
							: quantisAnual.valores.normal,
						chuvoso: isNuloOuUndefined(quantisAnual.valores.chuvoso)
							? '-'
							: quantisAnual.valores.chuvoso,
						muitoChuvoso: isNuloOuUndefined(quantisAnual.valores.muitoChuvoso)
							? '-'
							: quantisAnual.valores.muitoChuvoso,
						observado: isNuloOuUndefined(quantisAnual.valores.observado)
							? '-'
							: quantisAnual.valores.observado,
					});
				});
			} else {
				this.dadosAnual.quantis.map(quantisAnual => {
					tableData.push({
						regiao: this.dadosAnual.regiao,
						data: isNuloOuUndefined(quantisAnual.data)
							? '-'
							: quantisAnual.data,
						muitoSeco: isNuloOuUndefined(quantisAnual.valores.muitoSeco)
							? '-'
							: quantisAnual.valores.muitoSeco,
						seco: isNuloOuUndefined(quantisAnual.valores.seco)
							? '-'
							: quantisAnual.valores.seco,
						normal: isNuloOuUndefined(quantisAnual.valores.normal)
							? '-'
							: quantisAnual.valores.normal,
						chuvoso: isNuloOuUndefined(quantisAnual.valores.chuvoso)
							? '-'
							: quantisAnual.valores.chuvoso,
						muitoChuvoso: isNuloOuUndefined(quantisAnual.valores.muitoChuvoso)
							? '-'
							: quantisAnual.valores.muitoChuvoso,
						observado: isNuloOuUndefined(quantisAnual.valores.observado)
							? '-'
							: quantisAnual.valores.observado,
					});
				});
			}
		} else {
			this.dadosPeriodoChuvoso.quantis.forEach(quantis => {
				tableData.push({
					regiao: this.dadosPeriodoChuvoso.regiao,
					data: quantis.ano,
					muitoSeco: isNuloOuUndefined(quantis.muitoSeco)
						? '-'
						: quantis.muitoSeco,
					seco: isNuloOuUndefined(quantis.seco)
						? '-'
						: quantis.seco,
					normal: isNuloOuUndefined(quantis.normal)
						? '-'
						: quantis.normal,
					chuvoso: isNuloOuUndefined(quantis.chuvoso)
						? '-'
						: quantis.chuvoso,
					muitoChuvoso: isNuloOuUndefined(quantis.muitoChuvoso)
						? '-'
						: quantis.muitoChuvoso,
					observado: isNuloOuUndefined(quantis.observado)
						? '-'
						: quantis.observado,
				});
			});
		}

		return tableData;
	}

	private carregarImagem(src: string): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = err => reject(err);
			img.src = src;
		});
	}

	private async getLogoBase64(caminho: string): Promise<string | null> {
		try {
			const logo = await this.carregarImagem(caminho);
			const canvas = document.createElement('canvas');
			canvas.width = logo.width;
			canvas.height = logo.height;
			const ctx = canvas.getContext('2d')!;
			ctx.drawImage(logo, 0, 0);
			return canvas.toDataURL('image/png');
		} catch (e) {
			console.error(`[QUANTIS] Erro ao carregar logo ${caminho}:`, e);
			return null;
		}
	}

	private downloadDataUrl(nomeArquivo: string, dataUrl: string) {
		const link = document.createElement('a');
		link.href = dataUrl;
		link.download = nomeArquivo;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	private criarOpcoesEstaveisParaExportacao(): Highcharts.Options {
		return {
			chart: {
				width: 1200,
				height: 720,
				spacingTop: 24,
				spacingRight: 24,
				spacingBottom: 24,
				spacingLeft: 24,
			},
			title: {
				style: {
					fontSize: '22px',
					fontWeight: '600',
				},
			},
			caption: {
				style: {
					fontSize: '14px',
				},
			},
			legend: {
				itemStyle: {
					fontSize: '14px',
				},
			},
			xAxis: {
				labels: {
					style: {
						fontSize: '14px',
					},
				},
				title: {
					style: {
						fontSize: '16px',
						fontWeight: '600',
					},
				},
			},
			yAxis: {
				labels: {
					style: {
						fontSize: '14px',
					},
				},
				title: {
					style: {
						fontSize: '16px',
						fontWeight: '600',
					},
				},
			},
		};
	}

	async exportarPNG() {
		if (!this.chart) {
			this.toastr.error(
				'Não é possível exportar PNG pois nenhum gráfico foi gerado.'
			);
			return;
		}

		try {
			const chartBase64 = await criarImagemBase64FromChart(
				this.chart,
				1200,
				this.criarOpcoesEstaveisParaExportacao()
			);

			const [chartImg, logoAesaImg, logoGovernoImg] = await Promise.all([
				this.carregarImagem(chartBase64 as string),
				this.carregarImagem('assets/images/logos/logoAesa.svg'),
				this.carregarImagem('assets/images/logos/governo_pb.png'),
			]);

			const espacoRodape = Math.round(chartImg.height * 0.12);

			const canvas = document.createElement('canvas');
			canvas.width = chartImg.width;
			canvas.height = chartImg.height + espacoRodape;

			const ctx = canvas.getContext('2d')!;
			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, 0, canvas.width, canvas.height);

			ctx.drawImage(chartImg, 0, 0, chartImg.width, chartImg.height);

			const margem = canvas.width * 0.02;

			const larguraLogoAesa = Math.max(canvas.width * 0.08, 80);
			const alturaLogoAesa =
				(larguraLogoAesa / logoAesaImg.width) * logoAesaImg.height;

			const larguraLogoGoverno = Math.max(canvas.width * 0.1, 110);
			const alturaLogoGoverno =
				(larguraLogoGoverno / logoGovernoImg.width) * logoGovernoImg.height;

			const yLogoAesa = chartImg.height + (espacoRodape - alturaLogoAesa) / 2;
			const yLogoGoverno =
				chartImg.height + (espacoRodape - alturaLogoGoverno) / 2;

			ctx.drawImage(
				logoAesaImg,
				margem,
				yLogoAesa,
				larguraLogoAesa,
				alturaLogoAesa
			);

			ctx.drawImage(
				logoGovernoImg,
				canvas.width - larguraLogoGoverno - margem,
				yLogoGoverno,
				larguraLogoGoverno,
				alturaLogoGoverno
			);

			const dataUrl = canvas.toDataURL('image/png', 1);
			const nomeArquivo = `Quantis ${this.quantisTitulo} - ${this.periodoTitulo}.png`;

			this.downloadDataUrl(nomeArquivo, dataUrl);
		} catch (e) {
			console.error('[QUANTIS PNG] Erro ao exportar PNG:', e);
			this.toastr.error('Falha ao exportar PNG.');
		}
	}

	async exportarPDF() {
		if (
			!this.resultado ||
			!this.chart ||
			!this.resultado.dadosGraficosQuantis.length
		) {
			this.toastr.error(
				'Não é possível gerar um pdf pois nenhum gráfico foi gerado.'
			);
			return;
		}

		const documentDefinition: any = await pdfseira.documentDefinitions();

		const logoAesaBase64 = await this.getLogoBase64(
			'assets/images/logos/logoAesa.svg'
		);
		const logoGovernoBase64 = await this.getLogoBase64(
			'assets/images/logos/governo_pb.png'
		);

		if (logoAesaBase64 || logoGovernoBase64) {
			documentDefinition.images = documentDefinition.images || {};

			if (logoAesaBase64) {
				documentDefinition.images['aesaLogo'] = logoAesaBase64;
			}

			if (logoGovernoBase64) {
				documentDefinition.images['governoLogo'] = logoGovernoBase64;
			}

			documentDefinition.footer = (currentPage: number, pageCount: number) => {
				return {
					margin: [40, 0, 40, 20],
					columns: [
						logoAesaBase64
							? {
									image: 'aesaLogo',
									width: 70,
									alignment: 'left',
							  }
							: {},
						{
							text: `Página ${currentPage} de ${pageCount}`,
							alignment: 'center',
							fontSize: 8,
						},
						logoGovernoBase64
							? {
									image: 'governoLogo',
									width: 70,
									alignment: 'right',
							  }
							: {},
					],
				};
			};
		}

		documentDefinition.content.push([
			{
				text: `Quantis ${this.quantisTitulo} - ${this.periodoTitulo}`,
				alignment: 'center',
				margin: [0, 15, 5, 15],
			},
			{
				image: await criarImagemBase64FromChart(
					this.chart,
					1200,
					this.criarOpcoesEstaveisParaExportacao()
				),
				width: 500,
				height: 300,
				margin: [0, 15, 5, 15],
				alignment: 'center',
			},
		]);

		documentDefinition.content.push({
			table: {
				widths: [
					'15%',
					'12.5%',
					'12.5%',
					'10%',
					'12.5%',
					'12.5%',
					'12.5%',
					'12.5%',
				],
				body: this.getDadosTabelaParaExportacao(true),
				layout: {
					noWrap: false,
					fontSize: 5,
				},
			},
			fontSize: 10,
			marginLeft: 5,
		});

		baixarEAbrirPDF(
			`Quantis ${this.quantisTitulo} - ${this.periodoTitulo}`,
			documentDefinition
		);
	}

	exportarCSV() {
		const tableData = this.getDadosTabelaParaExportacao(false);
		DocumentExporter.gerarCSV(
			tableData,
			`Quantis ${this.quantisTitulo} - ${this.periodoTitulo}`
		);
	}

	exportarTXT() {
		const tableData = this.getDadosTabelaParaExportacao(false);
		let txtData = '';

		if (tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(
			txtData,
			`Quantis ${this.quantisTitulo} - ${this.periodoTitulo}`
		);
	}

	async exportarXLSX() {
		const nomeArquivo = `Quantis ${this.quantisTitulo} - ${this.periodoTitulo}`;
		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};
		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const headers: { label: string; text: string; size: number }[] = [];

		if (
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value ===
			'REGIAO_PLUVIOMETRICA'
		) {
			headers.push({
				label: 'regiao',
				text: 'Região pluviométrica',
				size: 24,
			});
		} else {
			headers.push({
				label: 'municipio',
				text: 'Município/Posto',
				size: 24,
			});
		}
		headers.push(
			{
				label: 'data',
				text: 'Data',
				size: 24,
			},
			{
				label: 'muitoSeco',
				text: 'Muito seco (mm)',
				size: 24,
			},
			{
				label: 'seco',
				text: 'Seco (mm)',
				size: 24,
			},
			{
				label: 'normal',
				text: 'Normal (mm)',
				size: 24,
			},
			{
				label: 'chuvoso',
				text: 'Chuvoso (mm)',
				size: 24,
			},
			{
				label: 'muitoChuvoso',
				text: 'Muito chuvoso (mm)',
				size: 24,
			},
			{
				label: 'observado',
				text: 'Observado (mm)',
				size: 24,
			}
		);

		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number,
						fromHeader: boolean,
						styleKeys: string[]
					) {
						if (data && data.label) {
							return 'headerStyleCenter';
						} else {
							if (colIndex > 0) {
								return 'rowStyleCenter';
							} else {
								return 'rowStyle';
							}
						}
					},
					headers: headers,
					data: this.getDadosTabelaParaExportacaoXLSX(),
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: nomeArquivo,
						styleId: 'title',
					},
				},
			],
			fileName: nomeArquivo,
		};

		await ExcelTable.generateExcel(dataExcel);
	}

	setValidatorsQuantisMensalOrAnual() {
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.ESTACAO)
			?.setValidators(Validators.required);
	}

	setValidatorsQuantisPluviometricoMensalOrAnual() {
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
			?.setValidators(Validators.required);
	}

	setValidatorsQuantisPluviometricoPeriodoChuvoso() {
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_CHUVOSO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
			?.setValidators(Validators.required);
	}

	clearValidators() {
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.ESTACAO)?.clearValidators();
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_CHUVOSO)
			?.clearValidators();
		this.inputs.form
			.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
			?.clearValidators();
	}
	alinharDadoPdf(data: any, isPdf: boolean) {
		if (isPdf) {
			return { text: data, alignment: 'center' };
		} else {
			return data;
		}
	}
}
